import dayjs from 'dayjs'
import { formatConsumptionValue } from './consumptionFormat'

/**
 * What a formatted column shows for one value. Excel writes a number or a
 * date as a real number or date cell that shows the same text.
 */
export type FormattedCell =
  | { kind: 'text'; text: string }
  | { kind: 'number'; text: string; value: number; excelFormat: string }
  | { kind: 'date'; text: string; value: Date; excelFormat: string }

/** One column's formatting, shared by its grid cell and its exports. */
export type CellFormat = (value: unknown) => FormattedCell

const NUMERAL = /^-?\d+(?:\.(\d+))?$/

// Excel keeps the text's decimals, so 1234.50 does not open as 1234.5.
const fromNumeral = (text: string): FormattedCell => {
  const decimals = NUMERAL.exec(text)
  if (!decimals) return { kind: 'text', text }
  const places = decimals[1]?.length ?? 0
  return {
    kind: 'number',
    text,
    value: Number(text),
    excelFormat: places ? `0.${'0'.repeat(places)}` : '0',
  }
}

const dateFormat =
  (pattern: string, excelFormat: string): CellFormat =>
  value => {
    if (value == null || value === '') return { kind: 'text', text: '-' }
    const date = dayjs(value as dayjs.ConfigType)
    if (!date.isValid()) return { kind: 'text', text: String(value) }
    return {
      kind: 'date',
      text: date.format(pattern),
      value: date.toDate(),
      excelFormat,
    }
  }

export const formatDateTimeCell = dateFormat(
  'DD/MM/YYYY HH:mm',
  'dd/mm/yyyy hh:mm'
)

export const formatDateCell = dateFormat('DD/MM/YYYY', 'dd/mm/yyyy')

export const formatThreeDecimalsCell: CellFormat = value =>
  fromNumeral(formatConsumptionValue(value, 3))
